import { X, Building2, CreditCard, Package, Calendar, User, Phone, Mail } from "lucide-react"
import { useLanguage } from "../context/LanguageProvider";

export default function LivreurInvoiceModal({ livreurInvoice, isOpen, onClose, isPrintMode = false }) {
  const { t } = useLanguage();
  
  const formatDateTime = (isoString) => {
    if (!isoString) return t('cors_livreur_invoice.na')
    const date = new Date(isoString)
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-100" },
      paid: { bg: "bg-green-600", text: "text-white", border: "border-green-600" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      annulé: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      pending_payment: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-100" },
    }

    const config = statusConfig[status?.toLowerCase()] || {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    }

    const displayStatus =
      {
        paid: t('cors_livreur_invoice.paid'),
        cancelled: t('cors_livreur_invoice.cancelled'),
        pending_payment: t('cors_livreur_invoice.pending_payment'),
      }[status?.toLowerCase()] || status

    return (
      <span
        className={`inline-flex items-center px-3 py-[1px] rounded-full text-[10px] font-medium border ${config.bg} ${config.text} ${config.border}`}
      >
        {displayStatus}
      </span>
    )
  }

  if (!isOpen) return null

  if (isPrintMode) {
    return <div className="fixed inset-0 bg-white z-50"></div>
  }

  if (!livreurInvoice) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">{t('cors_livreur_invoice.not_found')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 print:hidden bg-gradient-to-r from-teal-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Building2 className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('cors_livreur_invoice.invoice_title')} {livreurInvoice.invoice_number}</h2>
              <p className="text-xs text-gray-600">{t('cors_livreur_invoice.generated_on')} {formatDateTime(livreurInvoice.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 cursor-pointer hover:bg-red-400 hover:text-white rounded-full transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto custom-scrollbar max-h-[75vh] print:max-h-none print:overflow-visible">
          <div className="p-8 print:p-0 bg-white">
            {/* Header Section */}
            <div className="mb-8 print:mb-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-0.5">
                  <h1 className="text-xl font-bold text-gray-900">{t('cors_livreur_invoice.invoice_heading')}</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-600 ml-4">
                    <span className="font-medium">{t('cors_livreur_invoice.invoice_number_label')}</span>
                    <span className="font-mono bg-gray-100 px-3 py-1 text-xs rounded-lg">{livreurInvoice.invoice_number}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mb-4">{getStatusBadge(livreurInvoice.status)}</div>
                  <div className="flex text-xs gap-1 justify-end">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{t('cors_livreur_invoice.issue_date')} </span>
                    </div>
                    <div className="text-gray-900">{formatDateTime(livreurInvoice.created_at)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Livreur Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Livreur Details */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl px-6 py-4 border border-teal-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <User className="w-5 h-5 text-teal-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_livreur_invoice.delivery_info')}</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-gray-900">
                        {livreurInvoice.livreur?.name || "N/A"}
                      </div>
                      <div className="text-[10px] text-gray-600">
                        {livreurInvoice.livreur?.user?.email || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{livreurInvoice.livreur?.phone || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Banking Info */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl px-6 py-4 border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_livreur_invoice.bank_info')}</h3>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">{t('cors_livreur_invoice.rib')}</div>
                      <div className="text-xs font-mono text-gray-900">{livreurInvoice.livreur?.rib || "N/A"}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">{t('cors_livreur_invoice.fixed_delivery_rate')}</div>
                      <div className="text-xs font-semibold text-gray-900">
                        {Number.parseFloat(livreurInvoice.livreur?.prix_delivery || 0).toFixed(2)} MAD
                      </div>
                    </div>
                  </div>
                  {livreurInvoice.payment_date && (
                    <div className="flex items-center gap-1 pt-1 border-t border-emerald-100/50">
                      <div className="text-xs font-medium text-gray-600">{t('cors_livreur_invoice.paid_on')}</div>
                      <div className="text-[11px] text-gray-900">{formatDateTime(livreurInvoice.payment_date)}</div>
                      {livreurInvoice.payment_method && (
                        <div className="text-[11px] text-gray-500 ml-1">({livreurInvoice.payment_method})</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Orders Summary */}
            {livreurInvoice.livreur_invoice_orders && livreurInvoice.livreur_invoice_orders.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Package className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_livreur_invoice.delivered_orders')} ({livreurInvoice.livreur_invoice_orders_count})</h3>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="pl-6 pr-3 py-3 text-left text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.order')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.client')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.city')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.delivery_rate')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.order_total')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_livreur_invoice.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 text-xs">
                        {livreurInvoice.livreur_invoice_orders.map((lio) => (
                          <tr key={lio.id} className="hover:bg-gray-50 transition-colors">
                            <td className="pl-6 pr-3 py-3">
                              <div className="space-y-1">
                                <span className="font-mono text-[11px] bg-purple-100 px-2 py-0.5 rounded text-purple-700">
                                  #{lio.order?.code}
                                </span>
                                <div className="text-[10px] text-gray-500">
                                  {formatDateTime(lio.order?.created_at)}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center text-gray-700">
                              {lio.order?.nom || "N/A"}
                            </td>
                            <td className="px-3 py-3 text-center text-gray-700">
                              {lio.order?.ville || "N/A"}
                            </td>
                            <td className="px-3 py-3 text-center font-medium text-gray-900">
                              {Number.parseFloat(lio.order?.delivery_price || 0).toFixed(2)} MAD
                            </td>
                            <td className="px-3 py-3 text-center font-bold text-gray-900">
                              {Number.parseFloat(lio.order?.total || 0).toFixed(2)} MAD
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
                                {t('cors_livreur_invoice.delivered')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Earnings Summary */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg shadow-teal-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium opacity-90 text-teal-50">{t('cors_livreur_invoice.invoice_total')}</div>
                    <div className="text-xs opacity-75 mt-1">{t('cors_livreur_invoice.orders_included')} {livreurInvoice.livreur_invoice_orders_count}</div>
                  </div>
                  <div className="text-3xl font-bold">
                    {Number.parseFloat(livreurInvoice.total || 0).toFixed(2)} MAD
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center text-[10px] text-gray-400 mt-8 print:hidden italic">
              {t('cors_livreur_invoice.auto_generated')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
